import { HttpErrorResponse } from '@angular/common/http';
import {
  computed,
  DestroyRef,
  inject,
  Injectable,
  Provider,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  BdCompanyContextService,
  BdShareablesService,
} from '@bindoc-projects/bindoc-permissions-lib/data';
import {
  SHAREABLE_TYPE_DISPLAY_NAME,
  ShareableFolderObj,
} from '@bindoc-projects/resource-domain-lib';
import {
  catchError,
  EMPTY,
  map,
  Observable,
  of,
  skip,
  Subject,
  switchMap,
  take,
  tap,
} from 'rxjs';

import type {
  BreadcrumbEntry,
  FolderItem,
  ViewTab,
} from '../shareables-overview.models';
import {
  EMPTY_WORKSPACE_MEMORY,
  folderIdOf,
  isFolderTree,
  isScopeRoot,
  isSharedList,
  parseWorkspaceParams,
  reduceWorkspace,
  rememberWorkspace,
  sameLocation,
  sameScope,
  sharedCrumbs,
  toWorkspaceParams,
  treeCrumbs,
  WorkspaceIntent,
  WorkspaceLocation,
  WorkspaceMemory,
  WorkspaceRequest,
} from './workspace-location';
import {
  BD_WORKSPACE_LOCATION_SOURCE,
  MemoryWorkspaceLocationSource,
  routerWorkspaceLocationSource,
  WorkspaceLocationSource,
} from './workspace-location-source';

/**
 * - `idle` — the location shows no folder (recent, recycle bin, a list of shares).
 * - `forbidden` — a group root answered 403: the user is not a member.
 */
export type FolderListStatus =
  'idle' | 'loading' | 'ready' | 'error' | 'forbidden';

/** The folder on screen. `key` names the location it was loaded for. */
export interface FolderListState {
  readonly key: string | null;
  readonly status: FolderListStatus;
  readonly folder: ShareableFolderObj | null;
  readonly items: readonly FolderItem[];
}

/** Display names of the group on screen, for the breadcrumb and the header. */
export interface WorkspaceScopeNames {
  readonly groupId?: string;
  readonly groupName?: string;
  readonly companyName?: string;
}

const IDLE: FolderListState = {
  key: null,
  status: 'idle',
  folder: null,
  items: [],
};

/**
 * Identity of the folder a location shows — the load key. Two locations with
 * the same key show the same folder, so arriving at the second loads nothing.
 */
export function folderListKey(location: WorkspaceLocation): string | null {
  const folderId = folderIdOf(location);
  const scope =
    location.tab === 'group' ? `group:${location.groupId}` : location.tab;
  if (isFolderTree(location)) {
    return `tree|${scope}|${folderId ?? 'root'}`;
  }
  if (isSharedList(location) && folderId) {
    return `shared|${scope}|${folderId}`;
  }
  return null;
}

/** A folder's children as table rows. */
export function toFolderItems(folder: ShareableFolderObj): FolderItem[] {
  return folder.children.map((c) => ({
    id: c.shareableId,
    type: c.shareableType,
    isFolder: c.isFolder,
    name: c.name,
    typeLabel: SHAREABLE_TYPE_DISPLAY_NAME[c.shareableType] ?? c.shareableType,
    modifiedDate: c.modifiedDate,
    parentFolderId: folder.folderId,
    labels: c.labels,
    allowedPermissions: c.allowedShareablePermissions,
  }));
}

interface LoadResult {
  readonly key: string;
  readonly location: WorkspaceLocation;
  readonly folder?: ShareableFolderObj;
  readonly error?: unknown;
}

/**
 * The workspace's navigation, owned in one place.
 *
 * The location source (the URL, or memory in the resource picker) is the only
 * state. Every change goes the same way round:
 *
 *   intent → {@link reduceWorkspace} → write to the source
 *          → the source emits → parse → resolve → arrive → load
 *
 * Nothing is applied on the write, only on the emission. A re-emitted URL
 * therefore cannot move anything (it arrives at the location already shown),
 * and there is no "was this my own write?" bookkeeping to get wrong.
 *
 * Lists render {@link location}, {@link list} and {@link crumbs}; they never
 * load folders themselves.
 */
@Injectable()
export class BdWorkspaceNavigator {
  private readonly _source: WorkspaceLocationSource = inject(
    BD_WORKSPACE_LOCATION_SOURCE,
  );
  private readonly _shareables = inject(BdShareablesService);
  private readonly _companyContext = inject(BdCompanyContextService);
  private readonly _destroyRef = inject(DestroyRef);

  private readonly _location = signal<WorkspaceLocation>({ tab: 'recent' });
  private readonly _list = signal<FolderListState>(IDLE);
  private readonly _hint = signal<readonly BreadcrumbEntry[] | undefined>(
    undefined,
  );
  private readonly _highlight = signal<string | null>(null);
  private readonly _forbiddenGroupIds = signal<ReadonlySet<string>>(new Set());
  private readonly _names = signal<WorkspaceScopeNames>({});
  private readonly _sharedFolderIds = signal<ReadonlySet<string>>(new Set());

  /** Where the workspace is. */
  public readonly location = this._location.asReadonly();
  /** The folder on screen, while the location shows one. */
  public readonly list = this._list.asReadonly();
  /** A row to flash after "Im Ordner öffnen" landed next to it. */
  public readonly highlight = this._highlight.asReadonly();
  /** Groups that answered 403 — the sidebar locks them. */
  public readonly forbiddenGroupIds = this._forbiddenGroupIds.asReadonly();
  public readonly names = this._names.asReadonly();

  /**
   * The path below the list's own top: in a tree the folders below the scope
   * root, in a list of shares the folders from the shared one down. Shows the
   * clicked path right away and settles on the response once it lands.
   */
  public readonly crumbs = computed<BreadcrumbEntry[]>(() => {
    const location = this._location();
    const folderId = folderIdOf(location);
    const list = this._list();
    const hint = this._hint();
    if (!list.folder || list.key !== folderListKey(location)) {
      return folderId && hint?.at(-1)?.id === folderId ? [...hint] : [];
    }
    if (isFolderTree(location)) {
      return treeCrumbs(list.folder, hint);
    }
    if (isSharedList(location) && folderId) {
      return sharedCrumbs(list.folder, this._sharedFolderIds(), hint);
    }
    return [];
  });

  private _memory: WorkspaceMemory = EMPTY_WORKSPACE_MEMORY;
  private _arrived = false;
  private _namesFor: string | null = null;
  private _connected = false;
  private readonly _load$ = new Subject<{
    key: string;
    location: WorkspaceLocation;
  }>();
  private readonly _names$ = new Subject<string>();

  constructor() {
    this._load$
      .pipe(
        switchMap(({ key, location }) =>
          this._fetch(location).pipe(
            map((folder): LoadResult => ({ key, location, folder })),
            catchError((error: unknown) =>
              of<LoadResult>({ key, location, error }),
            ),
          ),
        ),
        takeUntilDestroyed(this._destroyRef),
      )
      .subscribe((result) => this._landed(result));

    this._names$
      .pipe(
        switchMap((groupId) =>
          this._shareables.group(groupId).pipe(
            switchMap((group) =>
              this._shareables.company(group.companyId).pipe(
                map((company) => ({
                  groupId,
                  groupName: group.name,
                  companyName: company.name,
                })),
                catchError(() =>
                  of({
                    groupId,
                    groupName: group.name,
                    companyName: undefined,
                  }),
                ),
              ),
            ),
            catchError(() => EMPTY),
          ),
        ),
        takeUntilDestroyed(this._destroyRef),
      )
      .subscribe((names) => {
        if (this._names().groupId === names.groupId) {
          this._names.set(names);
        }
      });
  }

  /**
   * Starts following the location source. Called once the host knows its
   * default tab — the one a URL without `?tab=` opens.
   */
  public connect(defaultTab: ViewTab = 'recent'): void {
    if (this._connected) {
      return;
    }
    this._connected = true;

    this._source.params$
      .pipe(
        map((params) => parseWorkspaceParams(params, defaultTab)),
        switchMap((request) => this._resolve(request)),
        takeUntilDestroyed(this._destroyRef),
      )
      .subscribe((location) => this._arrive(location));

    // Grants and roots are scoped by company: another company starts every
    // folder list at its top again.
    this._companyContext.selectedCompanyId$
      .pipe(skip(1), takeUntilDestroyed(this._destroyRef))
      .subscribe(() => {
        if (folderIdOf(this._location())) {
          this.goTo(null);
        } else {
          this.reload();
        }
      });
  }

  /** Applies an intent. `hint` is the path the user clicked through, if known. */
  public dispatch(
    intent: WorkspaceIntent,
    hint?: readonly BreadcrumbEntry[],
  ): void {
    const current = this._location();
    const { location, replaceUrl } = reduceWorkspace(
      current,
      intent,
      this._memory,
    );
    if (sameLocation(location, current)) {
      return;
    }
    this._hint.set(hint);
    this._source.write(toWorkspaceParams(location), { replaceUrl });
  }

  /** A sidebar entry. The group's name is shown until the full names arrive. */
  public selectTab(tab: ViewTab, groupId?: string, groupName?: string): void {
    if (tab === 'group' && groupId && this._names().groupId !== groupId) {
      this._names.set({ groupId, groupName });
    }
    this.dispatch({ type: 'select-tab', tab, groupId });
  }

  /** Opens a folder row of the list on screen, one level below the crumbs. */
  public openFolder(folder: BreadcrumbEntry): void {
    this.dispatch({ type: 'open-folder', folderId: folder.id }, [
      ...this.crumbs(),
      { id: folder.id, name: folder.name },
    ]);
  }

  /** A crumb by index, or `null` for the top of the list on screen. */
  public goTo(index: number | null): void {
    const crumbs = this.crumbs();
    const target = index === null ? undefined : crumbs[index];
    if (index !== null && !target) {
      return;
    }
    this.dispatch(
      { type: 'go-to', folderId: target?.id ?? null },
      target ? crumbs.slice(0, (index ?? 0) + 1) : undefined,
    );
  }

  /** One level up: the parent crumb, or the top of the list. */
  public up(): void {
    const depth = this.crumbs().length;
    this.goTo(depth <= 1 ? null : depth - 2);
  }

  /**
   * Follows a link whose scope is not known yet — a folder by id, or a
   * shareable whose folder should open with its row flashing. Resolved like any
   * other incoming link, so the result is the same as opening it in a new tab.
   */
  public openLink(
    target: { folderId: string } | { shareableId: string; groupId?: string },
  ): void {
    const groupId = 'shareableId' in target ? target.groupId : undefined;
    this._source.write(
      {
        tab: groupId ? 'group' : null,
        groupId: groupId ?? null,
        view: null,
        folderId: 'folderId' in target ? target.folderId : null,
        shareableId: 'shareableId' in target ? target.shareableId : null,
      },
      { replaceUrl: false },
    );
  }

  /** Re-reads the folder on screen (after a move, a share, a new folder). */
  public reload(): void {
    this._requestLoad(this._location(), true);
  }

  /** Patches the rows on screen in place (rename, labels, archive, a new file). */
  public patchItems(
    patch: (items: readonly FolderItem[]) => readonly FolderItem[],
  ): void {
    this._list.update((state) => ({ ...state, items: patch(state.items) }));
  }

  /**
   * The folders the list of shares on screen shares — reported by that list,
   * which owns the grants. They decide where a shared folder's path starts.
   */
  public setSharedFolderIds(ids: ReadonlySet<string>): void {
    this._sharedFolderIds.set(ids);
  }

  public clearHighlight(id: string): void {
    if (this._highlight() === id) {
      this._highlight.set(null);
    }
  }

  private _resolve(request: WorkspaceRequest): Observable<WorkspaceLocation> {
    switch (request.kind) {
      case 'location':
        return of(request.location);
      case 'resolve-folder':
        return this._resolveFolderLocation(request.folderId).pipe(
          tap((location) => this._writeCanonical(location)),
          switchMap(() => EMPTY),
        );
      case 'reveal-shareable':
        return this._resolveShareableLocation(
          request.shareableId,
          request.groupId,
        ).pipe(
          tap((location) => {
            this._highlight.set(request.shareableId);
            this._writeCanonical(location);
          }),
          switchMap(() => EMPTY),
        );
    }
  }

  /** `resolveFolderGroup` swallows its own failures into the user's scope. */
  private _resolveFolderLocation(
    folderId: string,
  ): Observable<WorkspaceLocation> {
    return this._shareables
      .resolveFolderGroup(folderId, this._companyContext.currentCompanyId)
      .pipe(
        take(1),
        map((group): WorkspaceLocation =>
          group
            ? { tab: 'group', groupId: group.id, view: 'folders', folderId }
            : { tab: 'shareables', folderId },
        ),
      );
  }

  /**
   * The shareable's folder, in the group the link names or the one it turns out
   * to live in. Without a folder (or when the lookup fails) the scope's root.
   */
  private _resolveShareableLocation(
    shareableId: string,
    groupId: string | undefined,
  ): Observable<WorkspaceLocation> {
    return this._shareables.sharedResourceById(shareableId).pipe(
      take(1),
      catchError(() => of(undefined)),
      switchMap((shareable): Observable<WorkspaceLocation> => {
        const folderId = shareable?.parentFolderId ?? null;
        if (groupId) {
          return of({ tab: 'group', groupId, view: 'folders', folderId });
        }
        if (!folderId) {
          return of({ tab: 'shareables', folderId: null });
        }
        return this._resolveFolderLocation(folderId);
      }),
    );
  }

  private _writeCanonical(location: WorkspaceLocation): void {
    this._source.write(toWorkspaceParams(location), { replaceUrl: true });
  }

  private _arrive(location: WorkspaceLocation): void {
    const previous = this._location();
    const first = !this._arrived;
    this._arrived = true;
    if (first || !sameLocation(previous, location)) {
      this._location.set(location);
      this._memory = rememberWorkspace(this._memory, location);
    }
    if (!first && !sameScope(previous, location)) {
      this._sharedFolderIds.set(new Set());
    }
    // A clicked path only describes the folder it was clicked to.
    const folderId = folderIdOf(location);
    if (this._hint() && this._hint()?.at(-1)?.id !== folderId) {
      this._hint.set(undefined);
    }
    if (location.tab === 'group' && this._namesFor !== location.groupId) {
      // Fetched once per group; a name the sidebar already knew stays meanwhile.
      this._namesFor = location.groupId;
      if (this._names().groupId !== location.groupId) {
        this._names.set({ groupId: location.groupId });
      }
      this._names$.next(location.groupId);
    }
    this._requestLoad(location, false);
  }

  private _requestLoad(location: WorkspaceLocation, force: boolean): void {
    const key = folderListKey(location);
    if (!key) {
      if (this._list().key !== null) {
        this._list.set(IDLE);
      }
      return;
    }
    const current = this._list();
    if (!force && current.key === key && current.status !== 'error') {
      return;
    }
    // The previous rows stay until the new ones land, under the spinner.
    this._list.set({ ...current, key, status: 'loading' });
    this._load$.next({ key, location });
  }

  private _fetch(location: WorkspaceLocation): Observable<ShareableFolderObj> {
    const folderId = folderIdOf(location);
    if (folderId) {
      return this._shareables.folder(folderId).pipe(take(1));
    }
    return location.tab === 'group'
      ? this._shareables.groupRootFolder(location.groupId).pipe(take(1))
      : this._shareables.rootFolder().pipe(take(1));
  }

  private _landed(result: LoadResult): void {
    if (this._list().key !== result.key) {
      return;
    }
    const { location, folder, error } = result;
    if (!folder) {
      const forbidden =
        error instanceof HttpErrorResponse &&
        error.status === 403 &&
        location.tab === 'group' &&
        folderIdOf(location) === null;
      if (forbidden && location.tab === 'group') {
        const groupId = location.groupId;
        this._forbiddenGroupIds.update((ids) =>
          ids.has(groupId) ? ids : new Set(ids).add(groupId),
        );
      }
      this._list.set({
        key: result.key,
        status: forbidden ? 'forbidden' : 'error',
        folder: null,
        items: [],
      });
      return;
    }

    // A link to a scope root by id: the root's canonical form has no folder id.
    // Settle under the root's key first, so the rewritten URL arrives at a
    // folder that is already on screen and loads nothing.
    if (
      isFolderTree(location) &&
      isScopeRoot(folder) &&
      folderIdOf(location) !== null
    ) {
      const root = { ...location, folderId: null } as WorkspaceLocation;
      this._list.set({
        key: folderListKey(root),
        status: 'ready',
        folder,
        items: toFolderItems(folder),
      });
      this._writeCanonical(root);
      return;
    }

    this._list.set({
      key: result.key,
      status: 'ready',
      folder,
      items: toFolderItems(folder),
    });
  }
}

/** The workspace page: navigation lives in the URL. */
export function provideWorkspaceNavigation(): Provider[] {
  return [
    {
      provide: BD_WORKSPACE_LOCATION_SOURCE,
      useFactory: routerWorkspaceLocationSource,
    },
    BdWorkspaceNavigator,
  ];
}

/** An embedding without a URL of its own (the resource picker). */
export function provideMemoryWorkspaceNavigation(): Provider[] {
  return [
    {
      provide: BD_WORKSPACE_LOCATION_SOURCE,
      useFactory: () => new MemoryWorkspaceLocationSource(),
    },
    BdWorkspaceNavigator,
  ];
}
